// web-frontend/src/components/common/Toast.jsx
// 🔥 전역 Toast 알림 컴포넌트
// 화면 중앙에 표시되는 간결한 토스트 메시지
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import './Toast.css';

// ==================== Toast Context ====================
const ToastContext = createContext(null);

// Toast 타입별 아이콘 및 스타일
const TOAST_CONFIG = {
  success: {
    icon: CheckCircle,
    className: 'toast-success',
  },
  error: {
    icon: XCircle,
    className: 'toast-error',
  },
  warning: {
    icon: AlertCircle,
    className: 'toast-warning',
  },
  info: {
    icon: Info,
    className: 'toast-info',
  },
};

// ==================== Toast Provider ====================
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Toast 추가
  const showToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random();
    
    const newToast = {
      id,
      message,
      type,
      duration,
    };

    setToasts(prev => [...prev, newToast]);

    // 자동 제거
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  // Toast 제거
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // 모든 Toast 제거
  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // 편의 메서드들
  const success = useCallback((message, duration) => {
    return showToast(message, 'success', duration);
  }, [showToast]);

  const error = useCallback((message, duration) => {
    return showToast(message, 'error', duration);
  }, [showToast]);

  const warning = useCallback((message, duration) => {
    return showToast(message, 'warning', duration);
  }, [showToast]);

  const info = useCallback((message, duration) => {
    return showToast(message, 'info', duration);
  }, [showToast]);

  const value = {
    toasts,
    showToast,
    removeToast,
    clearAllToasts,
    success,
    error,
    warning,
    info,
  };

  // [2026-07-02] 전역 toast 싱글톤 연결 (zustand store 등 컴포넌트 외부에서 사용) — FABLE P1-10
  useEffect(() => {
    setGlobalToastRef({ showToast });
    return () => setGlobalToastRef(null);
  }, [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// ==================== Toast Container ====================
const ToastContainer = ({ toasts, removeToast }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <ToastItem 
          key={toast.id} 
          toast={toast} 
          onClose={() => removeToast(toast.id)} 
        />
      ))}
    </div>
  );
};

// ==================== Toast Item ====================
const ToastItem = ({ toast, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);
  const config = TOAST_CONFIG[toast.type] || TOAST_CONFIG.info;
  const IconComponent = config.icon;

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300); // 애니메이션 시간
  };

  // 자동 종료 전 exit 애니메이션
  useEffect(() => {
    if (toast.duration > 0) {
      const exitTimer = setTimeout(() => {
        setIsExiting(true);
      }, toast.duration - 300);

      return () => clearTimeout(exitTimer);
    }
  }, [toast.duration]);

  return (
    <div className={`toast-item ${config.className} ${isExiting ? 'toast-exit' : 'toast-enter'}`}>
      <div className="toast-icon">
        <IconComponent size={20} />
      </div>
      <div className="toast-message">
        {toast.message}
      </div>
      <button className="toast-close" onClick={handleClose}>
        <X size={16} />
      </button>
    </div>
  );
};

// ==================== useToast Hook ====================
export const useToast = () => {
  const context = useContext(ToastContext);
  
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  
  return context;
};

// ==================== 전역 Toast 함수 (Context 외부에서 사용) ====================
let globalToastRef = null;

export const setGlobalToastRef = (ref) => {
  globalToastRef = ref;
};

export const toast = {
  show: (message, type, duration) => {
    if (globalToastRef) {
      return globalToastRef.showToast(message, type, duration);
    }
    console.warn('Toast: Provider not initialized');
    return null;
  },
  success: (message, duration) => toast.show(message, 'success', duration),
  error: (message, duration) => toast.show(message, 'error', duration),
  warning: (message, duration) => toast.show(message, 'warning', duration),
  info: (message, duration) => toast.show(message, 'info', duration),
};

export default ToastProvider;